/**
 * Workflows API client — browser-side only.
 *
 * Fetched from the browser, never during SSR: a server-side failure is
 * invisible (it just yields empty props) and the VM hairpinning to its own
 * external IP hits the firewall. From the browser every failure shows up in
 * the network tab and on screen with the real message.
 *
 * NEXT_PUBLIC_API_URL is inlined at build time — change it and rebuild.
 */
/**
 * Where the API lives, derived from the page's own hostname.
 *
 * The convention is <name>.domain → <name>-api.domain, so the app follows its
 * own domain without a rebuild. That matters here because NEXT_PUBLIC_* is
 * inlined at build time: pinning the host meant the frontend kept calling the
 * old API the moment the site moved to a new domain. An explicit
 * NEXT_PUBLIC_API_URL still wins if one is set.
 */
function defaultApiBase(): string {
  if (typeof window === "undefined") return "http://localhost:4000"
  const { protocol, hostname } = window.location
  // Local and bare-IP access talk to the API on its own port.
  if (hostname === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return `http://${hostname}:4000`
  }
  const [sub, ...rest] = hostname.split(".")
  if (!rest.length) return `${protocol}//${hostname}:4000`
  return `${protocol}//${sub}-api.${rest.join(".")}`
}

const BASE = process.env.NEXT_PUBLIC_API_URL || defaultApiBase()

/**
 * Supplies the Firebase ID token for API calls.
 *
 * Registered once by AuthProvider rather than imported from it, so this module
 * stays free of React and can be used anywhere. Without a token every call
 * now 401s — the API verifies independently of the UI gate.
 */
let tokenProvider: (() => Promise<string | null>) | null = null

/** Resolves once AuthProvider has registered a provider. */
let providerReady: Promise<void>
let markReady: () => void = () => {}
providerReady = new Promise<void>((r) => {
  markReady = r
})

export function setTokenProvider(fn: () => Promise<string | null>) {
  tokenProvider = fn
  markReady()
}

/**
 * Waits (briefly) for auth to be available before a request goes out.
 * Firebase restores a session asynchronously; without this, anything that
 * fetches on mount races it and gets a 401 that looks like a broken API.
 */
async function currentToken(): Promise<string | null> {
  await Promise.race([providerReady, new Promise((r) => setTimeout(r, 3000))])
  if (!tokenProvider) return null
  return tokenProvider()
}

export type Health = { ok: boolean; service: string; database: "up" | "down"; ts: string }

export type Verifier = {
  id: string
  fspUid: string
  email: string | null
  displayName: string | null
  status: string
  createdAt: string
}

export type SkillParam = {
  name: string
  type: "string" | "text" | "number" | "boolean" | "enum"
  required: boolean
  description: string
  options?: string[]
  default?: string | number | boolean
  example?: string
}

export type Skill = {
  id: string
  title: string
  category: "ai" | "fsp" | "data" | "comms"
  description: string
  effectful: boolean
  supportsDryRun: boolean
  system: boolean
  status: "active" | "degraded"
  defaultTimeoutMs: number
  params: SkillParam[]
}

export type SkillRun = {
  id: string
  skill: string
  status: string
  error: string | null
  startedAt: string
  finishedAt: string | null
  workflowRunId: string | null
}

export type Step =
  | { kind: "skill"; id: string; label: string; skill: string; input: Record<string, unknown> }
  | { kind: "loop"; id: string; label: string; over: (string | number)[]; as: string; steps: Step[] }
  | { kind: "repeat"; id: string; label: string; times: number; steps: Step[] }
  | { kind: "wait"; id: string; label: string; seconds: number }
  | {
      kind: "await_event"
      id: string
      label: string
      event: string
      timeoutSeconds: number
      match?: Record<string, string>
    }

export type Schedule =
  | { type: "manual" }
  | { type: "daily"; startTime: string; endTime?: string; timezone: string }
  | { type: "interval"; everyMinutes: number }
  | { type: "event"; event: string }

export type Risk = {
  level: "blocking" | "advisory"
  code: string
  title: string
  detail: string
  stepId?: string
}

export type Effort = "fast" | "balanced" | "deep"

export type WorkflowSpec = {
  name: string
  description: string
  schedule: Schedule
  steps: Step[]
}

export type Workflow = {
  id: string
  name: string
  description: string | null
  prompt: string
  spec: WorkflowSpec
  enabled: boolean
  testMode: boolean
  createdBy: string | null
  archived: boolean
  createdAt: string
  updatedAt: string
}

export type WorkflowRun = {
  id: string
  workflowId: string
  status: string
  testMode: boolean
  trigger: string
  stepsTotal: number
  stepsDone: number
  error: string | null
  logs: string[]
  /** Per-step live state, drives the flow chart highlight. */
  stepStates: Record<string, "running" | "succeeded" | "failed" | "skipped">
  /** Iteration counter per looped step. */
  stepCounts: Record<string, number>
  startedAt: string
  finishedAt: string | null
}

export type Res<T> = { data: T | null; error: string | null }

async function request<T>(path: string, init?: RequestInit): Promise<Res<T>> {
  const url = `${BASE}${path}`
  try {
    const token = await currentToken()
    const headers: Record<string, string> = {}
    if (init?.body) headers["Content-Type"] = "application/json"
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, {
      cache: "no-store",
      // Without a timeout a dropped packet (a closed firewall port drops rather
      // than refuses) leaves the promise pending and the UI stuck on "loading".
      signal: AbortSignal.timeout(init?.method === "POST" ? 300_000 : 15_000),
      ...init,
      headers,
    })
    const text = await res.text()
    let json: unknown = null
    try {
      json = JSON.parse(text)
    } catch {
      /* non-JSON */
    }
    if (!res.ok) {
      const msg = (json as { error?: string; problems?: string[] } | null)
      const detail = msg?.problems?.length ? ` (${msg.problems.join("; ")})` : ""
      return { data: null, error: `${msg?.error ?? `HTTP ${res.status}`}${detail}` }
    }
    return { data: json as T, error: null }
  } catch (e) {
    const timedOut = e instanceof DOMException && e.name === "TimeoutError"
    return {
      data: null,
      error: timedOut
        ? `Timed out reaching ${url} — the host is likely unreachable.`
        : `Could not reach ${url} — ${e instanceof Error ? e.message : String(e)}`,
    }
  }
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined })

export const api = {
  base: BASE,

  health: () => request<Health>("/health"),
  verifiers: () => request<{ count: number; verifiers: Verifier[] }>("/api/verifiers"),

  skills: () => request<{ skills: Skill[] }>("/api/skills"),
  skillRuns: (limit = 50) => request<{ count: number; runs: SkillRun[] }>(`/api/skill-runs?limit=${limit}`),
  runSkill: (id: string, input: unknown, dryRun = false) =>
    post<{ runId: string; status: string; output: unknown; logs: string[] }>(
      `/api/skills/${encodeURIComponent(id)}/run`,
      { input, dryRun },
    ),

  workflows: (archived = false) =>
    request<{ count: number; workflows: Workflow[] }>(`/api/workflows?archived=${archived}`),
  workflow: (id: string) =>
    request<{ workflow: Workflow; runs: WorkflowRun[] }>(`/api/workflows/${id}`),
  generate: (body: {
    prompt: string
    context?: string
    allowedSkills?: string[]
    effort?: Effort
  }) =>
    post<{
      // Always an array — a request needing two triggers becomes two workflows.
      workflows: { spec: WorkflowSpec; risks: Risk[] }[]
      reasoning: string
      risks: Risk[]
      costUsd: number | null
    }>("/api/workflows/generate", body),
  saveWorkflow: (body: {
    prompt: string
    specs: WorkflowSpec[]
    createdBy?: string
    acknowledgeRisks?: boolean
  }) => post<{ workflows: Workflow[] }>("/api/workflows", body),
  /** Edit the parts a person authors: title, description, and the plan itself. */
  updateWorkflow: (
    id: string,
    patch: { name?: string; description?: string; spec?: WorkflowSpec },
  ) =>
    request<{ workflow: Workflow }>(`/api/workflows/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  setSwitches: (id: string, patch: { enabled?: boolean; testMode?: boolean; archived?: boolean }) =>
    request<{ workflow: Workflow }>(`/api/workflows/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  runWorkflow: (id: string) => post<{ runId: string }>(`/api/workflows/${id}/run`),
  /** Permanent. Takes the run history with it — archive is the reversible option. */
  deleteWorkflow: (id: string) =>
    request<{ deleted: { workflow: string; runs: number } }>(`/api/workflows/${id}`, {
      method: "DELETE",
    }),
  workflowRun: (id: string) => request<{ run: WorkflowRun }>(`/api/workflow-runs/${id}`),
  stopRun: (id: string) => post<{ ok: boolean }>(`/api/workflow-runs/${id}/stop`),
}
